import React from 'react';
import { shape, string, arrayOf, func } from 'prop-types';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { Pill } from '@andes/badge';
import colornames from '../../lib/colornames';
import IconFactory from '../icons/factory-builder';
import labelToProps from '../styled-label/label-to-props';
import StyledLabel from '../styled-label';
import withMappedProps from '../../utils/withMappedProps';
import { validateSnackbarColor } from '../../utils/validators';
import VehicleHistoryModal from '../vehicle-history-modal';
import { serializeLabel } from '../../utils/label-utils';

const LabelComponent = withMappedProps(StyledLabel)(labelToProps);

const namespace = 'ui-pdp-vehicle-history-specs';

/**
 * Function to clean and return the key of the attribute div
 * @param Badge Object
 * @returns {<Pill />}
 */
const lowercaseBadge = badge => (
  <Pill
    className={`${namespace}__pill`}
    color={validateSnackbarColor(badge.type.toLowerCase())}
    size={badge.font_size.toLowerCase()}
    hierarchy={badge.hierarchy.toLowerCase()}
    roundedCorners={['top-right', 'top-left', 'bottom-right', 'bottom-left']}
  >
    {badge.text}
  </Pill>
);

const VehicleHistorySpecs = ({
  header,
  badge,
  sections,
  description,
  fake_door_button,
  itemId,
  modalComponents,
  fetchModal,
}) => (
  <div className={classnames(namespace)}>
    <StyledLabel as="h2" className={classnames({ [`${namespace}__header`]: isEmpty(description) })} {...header}>
      {badge && lowercaseBadge(badge)}
    </StyledLabel>
    {!isEmpty(description) && <StyledLabel as="p" className={`${namespace}__description`} {...description} />}
    <div className={`${namespace}__specs-list`}>
      {sections[0].attributes[0].label &&
        sections[0].attributes.map(attr => (
          <>
            <div key={serializeLabel(attr.label.text)} className={`${namespace}__spec`}>
              <div aria-hidden="true" className={`${namespace}__icon`}>
                {IconFactory(attr.icon.id, classnames(colornames(attr.icon.color)))}
              </div>
              <LabelComponent className={`${namespace}__spec_text`} label={attr.label} />
            </div>
          </>
        ))}
    </div>
    {fake_door_button && (
      <div className={`${namespace}__vehicle-historic-complete`}>
        <VehicleHistoryModal
          itemId={itemId}
          icon={fake_door_button.icon}
          label={fake_door_button.label.text}
          track={fake_door_button.track}
          modalComponents={modalComponents}
          fetchModal={fetchModal}
        />
      </div>
    )}
  </div>
);

VehicleHistorySpecs.propTypes = {
  itemId: string,
  header: shape({
    text: string.isRequired,
    color: string,
    font_size: string,
    font_family: string,
  }).isRequired,
  description: shape({
    text: string.isRequired,
    color: string,
    font_size: string,
    font_family: string,
  }).isRequired,
  badge: shape({
    text: string,
    font_size: string,
    type: string,
    border: string,
    hierarchy: string,
  }),
  sections: arrayOf(
    shape({
      attributes: arrayOf(
        shape({
          icon: shape({
            id: string.isRequired,
            color: string,
            size: string,
          }),
          label: shape({
            text: string.isRequired,
            color: string,
            font_size: string,
            font_family: string,
            values: shape({
              value_text: shape({
                text: string,
                color: string,
                font_size: string,
                font_family: string,
              }),
            }),
          }),
        }),
      ),
    }),
  ).isRequired,
  fake_door_button: shape({
    type: string,
    target: string,
    label: shape({
      text: string,
    }),
    icon: shape({
      id: string,
      color: string,
    }),
    action_type: string,
    track: shape({ melidata_event: shape({}) }),
  }),
  modalComponents: shape({}),
  fetchModal: func,
};

VehicleHistorySpecs.defaultProps = {
  header: {},
  description: {},
  badge: null,
  sections: [],
};

export default React.memo(VehicleHistorySpecs);
